import type { ErrorObject, Validation, ValidationArgs } from '@vuelidate/core'
import type { Ref } from 'vue'

/** Функция для создания списка возможных сообщений ошибок
 * @param v$ объект vuelidate
 * @param rules объект правил с ошибками для vuelidate
 * @returns объект с текстами ошибок для невалидных полей. Имеет структуру полей как у состояния v$.
 * @example
 *
 * ```typescript
 * const state = {
 *   a: 1,
 * };
 * const rules = {
 *   a: { required: withMessage("Обязательное поле", required) },
 * };
 * const v$ = useVuelidate(rules, state);
 * const errorMessages = useErrorMessages(v$, rules); /// { a: { required: "Обязательное поле" } }
 * ```
 *
 */
const useErrorMessages = <T, R extends ValidationArgs<T> = ValidationArgs<T>>(
  v$: Ref<Validation<R, T>>,
  rules: R,
) => {
  const getError = (key: keyof R) =>
    (v$.value[key]?.$errors as ErrorObject[]).map(({ $message }) =>
      unref($message),
    )

  const keys = Object.keys(rules) as Array<keyof R>

  return keys.reduce<Dictionary<string[]>>((acc, key) => {
    acc[key as string] = getError(key) || []

    return acc
  }, {}) as Record<keyof R, string[]>
}

export { useErrorMessages }
